import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';

import { TEXT_DARK, TEXT_SECONDARY, SERIF_STACK } from '../tokens';

const AVATAR_SIZE = { xs: 130, sm: 180 };

const AVATAR_BASE = {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: '24px',
  boxShadow: '0 2px 10px rgba(0,0,0,.12)',
  flexShrink: 0,
};

// The square portrait/logo that opens a hero page's identity header. Portraits
// fill the box ('cover'); logos sit on white and keep their proportions
// ('contain'). Without an image the box falls back to initials on `background`.
export function HeroAvatar({
  imageUrl, fit, background, children,
}) {
  if (imageUrl) {
    return (
      <Box sx={{
        ...AVATAR_BASE,
        ...(fit === 'contain' && { background: '#fff' }),
        backgroundImage: `url("${imageUrl}")`,
        backgroundSize: fit,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }} />
    );
  }

  return (
    <Box sx={{
      ...AVATAR_BASE,
      background,
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: SERIF_STACK,
      fontWeight: 700,
      fontSize: { xs: '3rem', sm: '4rem' },
    }}>
      {children}
    </Box>
  );
}

HeroAvatar.propTypes = {
  imageUrl: PropTypes.string,
  fit: PropTypes.oneOf(['cover', 'contain']),
  background: PropTypes.string,
  children: PropTypes.node,
};

HeroAvatar.defaultProps = {
  imageUrl: null,
  fit: 'cover',
  background: TEXT_SECONDARY,
  children: null,
};

export default function HeroIdentityHeader({
  avatar, title, subtitle, links,
}) {
  return (
    <Box sx={{ flexShrink: 0 }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: { xs: '16px', sm: '24px' },
      }}>
        {avatar}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box component="h1" sx={{
            fontFamily: SERIF_STACK,
            fontWeight: 700,
            fontSize: { xs: '2.2rem', sm: '2.3rem' },
            lineHeight: 1.2,
            color: TEXT_DARK,
            m: 0,
            mt: '-0.2em',
            overflowWrap: 'anywhere',
          }}>
            {title}
          </Box>
          {subtitle && (
            <Box sx={{
              fontSize: '1rem', color: '#8b8f96', fontWeight: 500, mt: '3px',
            }}>
              {subtitle}
            </Box>
          )}
          {links && (
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '15px', mt: '18px',
            }}>
              {links}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

HeroIdentityHeader.propTypes = {
  avatar: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  links: PropTypes.node,
};

HeroIdentityHeader.defaultProps = {
  avatar: null,
  title: null,
  subtitle: null,
  links: null,
};
